import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    DollarOutlined,
    LinkOutlined,
} from '@ant-design/icons';
import {
    Table as AntTable,
    Button,
    Card,
    Col,
    Flex,
    Row,
    Tag,
    Typography,
} from 'antd';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';

import { InvoiceDto, InvoiceShiftDto, ShiftType } from '@clh/api-client';
import {
    calculateTotalStipend,
    HumlaDateTime,
    roundToAtMostTwoDecimals,
    toDollarAmount,
} from '@clh/util';

import { useApiClient } from '../hooks/use-api-client';
import Spinner from '../spinner';

import usePayPeriodContext from './pay-period-context';

const { Text } = Typography;

const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(amount / 100);

const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
        case 'paid':
            return 'green';
        case 'open':
            return 'blue';
        case 'draft':
            return 'orange';
        case 'void':
        case 'uncollectible':
            return 'red';
        default:
            return 'default';
    }
};

const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
        case 'paid':
            return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
        case 'open':
            return <DollarOutlined style={{ color: '#1890ff' }} />;
        case 'void':
        case 'uncollectible':
            return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
        default:
            return null;
    }
};

function StripeApiDataComponent({
    invoice,
    totalCostPlatform,
}: {
    invoice: InvoiceDto;
    totalCostPlatform: number;
}) {
    return (
        <Card size="small" style={{ height: '100%' }}>
            <Flex
                justify="space-between"
                align="center"
                style={{ marginBottom: 16 }}
            >
                <Typography.Text strong style={{ color: '#fa8c16' }}>
                    STRIPE API DATA
                </Typography.Text>
            </Flex>
            <Row gutter={[16, 16]}>
                <Col span={8}>
                    <Text type="secondary">Invoice ID:</Text>
                    <div>
                        <Text code={true} copyable={true}>
                            {invoice.stripeInvoiceId}
                        </Text>
                    </div>
                </Col>
                <Col span={8}>
                    <Text type="secondary">Status:</Text>
                    <div>
                        <Tag color={getStatusColor(invoice.status)}>
                            {invoice.status.toUpperCase()}
                        </Tag>
                    </div>
                </Col>
                <Col span={8}>
                    <Text type="secondary">Total Cost:</Text>
                    <Flex align="center" gap={8}>
                        <div style={{ fontWeight: 500 }}>
                            {formatCurrency(invoice.totalCost)}
                        </div>
                        {invoice.totalCost === totalCostPlatform ? (
                            <CheckCircleOutlined
                                style={{ color: '#52c41a', fontSize: '16px' }}
                                title="Matches platform amount"
                            />
                        ) : (
                            <>
                                <CloseCircleOutlined
                                    style={{
                                        color: '#ff4d4f',
                                        fontSize: '16px',
                                    }}
                                    title="Does not match platform amount"
                                />
                                <Text
                                    type="danger"
                                    style={{ fontSize: '12px' }}
                                >
                                    Mismatch
                                </Text>
                            </>
                        )}
                    </Flex>
                </Col>
                <Col span={8}>
                    <Text type="secondary">Invoice Date:</Text>
                    <div>
                        {DateTime.fromJSDate(
                            invoice.invoiceDate
                        ).toLocaleString(DateTime.DATE_SHORT)}
                    </div>
                </Col>
                <Col span={8}>
                    <Text type="secondary">Due Date:</Text>
                    <div>
                        {DateTime.fromJSDate(invoice.dueDate).toLocaleString(
                            DateTime.DATE_SHORT
                        )}
                    </div>
                </Col>
                <Col span={8}>
                    <Text type="secondary">PDF Link:</Text>
                    <div>
                        {invoice.pdfLink ? (
                            <Button
                                type="link"
                                size="small"
                                href={invoice.pdfLink}
                                target="_blank"
                                rel="noreferrer"
                                icon={<LinkOutlined />}
                                style={{ padding: 0, height: 'auto' }}
                            >
                                View PDF
                            </Button>
                        ) : (
                            <Text type="secondary">-</Text>
                        )}
                    </div>
                </Col>
            </Row>
        </Card>
    );
}

function InvoiceShiftDetails({
    shifts,
    totalCost,
}: {
    shifts: InvoiceShiftDto[];
    totalCost: number;
}) {
    return (
        <Flex justify="space-between">
            <AntTable<InvoiceShiftDto>
                size="small"
                showHeader={true}
                pagination={false}
                footer={() => (
                    <Flex justify="end">
                        <div style={{ marginRight: 10 }}>
                            Total Cost: <br />
                            (platform)
                        </div>
                        <Typography.Title
                            level={5}
                            style={{ padding: 0, margin: 0 }}
                        >
                            {formatCurrency(totalCost)}
                        </Typography.Title>
                    </Flex>
                )}
                columns={[
                    {
                        dataIndex: 'facility',
                        title: 'Facility',
                        render: (facility, shift) => (
                            <Button
                                type="link"
                                target="_blank"
                                href={`/shifts/${shift.id}`}
                                icon={<LinkOutlined />}
                                style={{ padding: 0, height: 'auto' }}
                            >
                                {facility.name}
                            </Button>
                        ),
                        ellipsis: true,
                    },
                    {
                        dataIndex: 'nurseProfile',
                        title: 'Nurse',
                        render: (nurseProfile) =>
                            nurseProfile && (
                                <Button
                                    type="link"
                                    target="_blank"
                                    href={`/profiles/${nurseProfile?.id}`}
                                    style={{ padding: 0, height: 'auto' }}
                                >
                                    {nurseProfile.firstName}{' '}
                                    {nurseProfile.lastName}
                                </Button>
                            ),
                        ellipsis: true,
                    },
                    {
                        dataIndex: 'specialtyCode',
                        render: (_, shift) => (
                            <>
                                {shift.specialtyCode && (
                                    <Tag>{shift.specialtyCode}</Tag>
                                )}
                                <Tag>{shift.shiftType.toLowerCase()}</Tag>
                            </>
                        ),
                    },
                    {
                        dataIndex: 'startTime',
                        width: 280,
                        title: 'Scheduled time',
                        render: (_startTime, shift) => (
                            <Typography.Text type="secondary">
                                {DateTime.fromJSDate(
                                    shift.startTime
                                ).toLocaleString(
                                    HumlaDateTime.DATETIME_SHORT
                                )}{' '}
                                -{' '}
                                {DateTime.fromJSDate(
                                    shift.endTime
                                ).toLocaleString(HumlaDateTime.DATETIME_SHORT)}
                            </Typography.Text>
                        ),
                    },
                    {
                        title: 'Hours Worked',
                        dataIndex: 'payPeriodMinutesWorked',
                        render: (payPeriodMinutesWorked, shift) => {
                            const totalMinutesWorked =
                                (shift.minutesWorked || 0) +
                                (shift.onCallMinutesWorked || 0);
                            const totalPayPeriodMinutesWorked =
                                (payPeriodMinutesWorked || 0) +
                                (shift.payPeriodOnCallMinutesWorked || 0);
                            return (
                                <>
                                    {roundToAtMostTwoDecimals(
                                        (totalPayPeriodMinutesWorked || 0) / 60
                                    )}
                                    {totalPayPeriodMinutesWorked <
                                        totalMinutesWorked && ' (Partial)'}
                                </>
                            );
                        },
                    },
                    {
                        render: (_, shift) => {
                            const cellStyle = {
                                textAlign: 'center' as const,
                                lineHeight: '12px',
                                paddingLeft: 8,
                                paddingRight: 8,
                            };

                            const isOnCall =
                                shift.shiftType === ShiftType.OnCall &&
                                shift.onCallShift;

                            return (
                                <Flex justify="end">
                                    <table style={{ width: 'auto' }}>
                                        <tbody>
                                            <tr>
                                                {isOnCall ? (
                                                    <>
                                                        <td style={cellStyle}>
                                                            {roundToAtMostTwoDecimals(
                                                                (shift.onCallShift!
                                                                    .minutesWorked ||
                                                                    0) / 60
                                                            )}
                                                            <br />
                                                            <small>
                                                                hrs on-call
                                                            </small>
                                                        </td>
                                                        <td>×</td>
                                                        <td style={cellStyle}>
                                                            {toDollarAmount(
                                                                shift.onCallShift!
                                                                    .totalRate
                                                            )}
                                                            <br />
                                                            <small>
                                                                on-call rate
                                                            </small>
                                                        </td>
                                                        <td>+</td>
                                                        <td style={cellStyle}>
                                                            {roundToAtMostTwoDecimals(
                                                                (shift.payPeriodMinutesWorked ||
                                                                    0) / 60
                                                            )}
                                                            <br />
                                                            <small>
                                                                hrs Called back
                                                            </small>
                                                        </td>
                                                        <td>×</td>
                                                        <td style={cellStyle}>
                                                            {toDollarAmount(
                                                                shift.totalRate
                                                            )}
                                                            <br />
                                                            <small>
                                                                Called back rate
                                                            </small>
                                                        </td>
                                                        {shift.stipends &&
                                                        shift.stipends.length >
                                                            0 &&
                                                        shift.isFirstPayPeriod ? (
                                                            <>
                                                                <td>+</td>
                                                                <td
                                                                    style={
                                                                        cellStyle
                                                                    }
                                                                >
                                                                    {toDollarAmount(
                                                                        calculateTotalStipend(
                                                                            shift.stipends?.filter(
                                                                                (
                                                                                    s
                                                                                ) =>
                                                                                    s.qualified
                                                                            )
                                                                        )
                                                                    )}
                                                                    <br />
                                                                    <small>
                                                                        stipend
                                                                    </small>
                                                                </td>
                                                            </>
                                                        ) : null}
                                                        <td>=</td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td style={cellStyle}>
                                                            {roundToAtMostTwoDecimals(
                                                                (shift.payPeriodMinutesWorked ||
                                                                    0) / 60
                                                            )}
                                                            <br />
                                                            <small>hrs</small>
                                                        </td>
                                                        <td>×</td>
                                                        <td style={cellStyle}>
                                                            {toDollarAmount(
                                                                shift.totalRate
                                                            )}
                                                            <br />
                                                            <small>
                                                                total rate
                                                            </small>
                                                        </td>
                                                        {shift.stipends &&
                                                        shift.stipends.length >
                                                            0 &&
                                                        shift.isFirstPayPeriod ? (
                                                            <>
                                                                <td>+</td>
                                                                <td
                                                                    style={
                                                                        cellStyle
                                                                    }
                                                                >
                                                                    {toDollarAmount(
                                                                        calculateTotalStipend(
                                                                            shift.stipends?.filter(
                                                                                (
                                                                                    s
                                                                                ) =>
                                                                                    s.qualified
                                                                            )
                                                                        )
                                                                    )}
                                                                    <br />
                                                                    <small>
                                                                        stipend
                                                                    </small>
                                                                </td>
                                                            </>
                                                        ) : null}
                                                        <td>=</td>
                                                    </>
                                                )}
                                            </tr>
                                        </tbody>
                                    </table>
                                </Flex>
                            );
                        },
                    },
                    {
                        dataIndex: 'totalRate',
                        title: 'Total Cost',
                        width: 100,
                        render: (_totalRate, shift) => {
                            let totalCost: number;

                            if (
                                shift.shiftType === ShiftType.OnCall &&
                                shift.onCallShift
                            ) {
                                // On-Call shift calculation
                                const onCallCost =
                                    (shift.onCallShift.totalRate *
                                        (shift.onCallShift.minutesWorked ||
                                            0)) /
                                    60;
                                const calledInCost =
                                    (shift.totalRate *
                                        (shift.payPeriodMinutesWorked || 0)) /
                                    60;
                                const stipend = calculateTotalStipend(
                                    shift.isFirstPayPeriod
                                        ? shift.stipends?.filter(
                                              (s) => s.qualified
                                          )
                                        : []
                                );
                                totalCost = onCallCost + calledInCost + stipend;
                            } else {
                                // Standard shift calculation
                                totalCost =
                                    (shift.totalRate *
                                        (shift.payPeriodMinutesWorked || 0)) /
                                        60 +
                                    calculateTotalStipend(
                                        shift.isFirstPayPeriod
                                            ? shift.stipends?.filter(
                                                  (s) => s.qualified
                                              )
                                            : []
                                    );
                            }

                            return <b>{formatCurrency(totalCost)}</b>;
                        },
                    },
                ]}
                dataSource={shifts}
            />
        </Flex>
    );
}

function InvoiceCard({ invoice }: { invoice: InvoiceDto }) {
    const totalCostPlatform = invoice.shifts.reduce(
        (total, shift) =>
            total +
            (shift.totalRate * (shift.payPeriodMinutesWorked || 0)) / 60 +
            calculateTotalStipend(shift.stipends?.filter((s) => s.qualified)),
        0
    );

    return (
        <Card
            key={invoice.stripeInvoiceId}
            size="small"
            style={{ marginBottom: 16 }}
        >
            <Flex
                justify="space-between"
                align="center"
                style={{ marginBottom: 16 }}
            >
                <Flex align="center" gap={8}>
                    {getStatusIcon(invoice.status)}
                    <Typography.Title level={5} style={{ margin: 0 }}>
                        {invoice.facilityName}
                    </Typography.Title>
                </Flex>
                <Button
                    href={`https://dashboard.stripe.com/invoices/${invoice.stripeInvoiceId}`}
                    target="_blank"
                    rel="noreferrer"
                    size="small"
                    icon={<LinkOutlined />}
                >
                    Open in Stripe
                </Button>
            </Flex>

            <Card
                size="small"
                style={{
                    backgroundColor: '#f8f9ff',
                    marginBottom: 12,
                }}
            >
                <Typography.Text
                    strong
                    style={{
                        color: '#000',
                        marginBottom: 8,
                        display: 'block',
                    }}
                >
                    {invoice.shifts.length} Shift
                    {invoice.shifts.length !== 1 && 's'}
                </Typography.Text>
                <InvoiceShiftDetails
                    shifts={invoice.shifts}
                    totalCost={totalCostPlatform}
                />
            </Card>

            <StripeApiDataComponent
                invoice={invoice}
                totalCostPlatform={totalCostPlatform}
            />
        </Card>
    );
}

function InvoicesSummary({ invoices }: { invoices: InvoiceDto[] }) {
    const totalInvoices = invoices.length;
    const totalShifts = invoices.reduce(
        (total, invoice) => total + invoice.shifts.length,
        0
    );
    const totalInvoiceAmount = invoices.reduce(
        (total, invoice) => total + invoice.totalCost,
        0
    );

    return (
        <Card size="small" style={{ marginBottom: 16 }}>
            <Flex justify="space-between" align="center">
                <Typography.Title level={5} style={{ margin: 0, padding: 0 }}>
                    Invoices Summary
                </Typography.Title>
                <Flex gap={24}>
                    <div style={{ textAlign: 'center' }}>
                        <div
                            style={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: '#1890ff',
                            }}
                        >
                            {formatCurrency(totalInvoiceAmount)}
                        </div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Total Invoiced $
                        </Text>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div
                            style={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: '#fa8c16',
                            }}
                        >
                            {totalInvoices}
                        </div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Total Invoices
                        </Text>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div
                            style={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: '#52c41a',
                            }}
                        >
                            {totalShifts}
                        </div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Total Shifts
                        </Text>
                    </div>
                </Flex>
            </Flex>
        </Card>
    );
}

export default function Invoices() {
    const api = useApiClient();
    const [invoices, setInvoices] = useState<InvoiceDto[]>([]);
    const [loading, setLoading] = useState(false);

    const { startDate: payPeriodStart, endDate: payPeriodEnd } =
        usePayPeriodContext();

    const fetchInvoices = async () => {
        if (!api) return;

        setLoading(true);
        try {
            const invoiceData = await api.billingInvoiceControllerGetInvoices({
                payPeriodStart: payPeriodStart.toISOString(),
                payPeriodEnd: payPeriodEnd.toISOString(),
            });

            setInvoices(invoiceData);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchInvoices();
    }, [payPeriodStart.toISOString(), payPeriodEnd.toISOString()]);

    if (!api) {
        return <Spinner />;
    }

    if (loading) {
        return <Spinner />;
    }

    return (
        <>
            <InvoicesSummary invoices={invoices} />
            {invoices.map((invoice) => (
                <InvoiceCard key={invoice.stripeInvoiceId} invoice={invoice} />
            ))}
        </>
    );
}
